import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Modal,
  Paper,
  Progress,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconFile,
  IconAlertCircle,
  IconClock,
  IconFileUpload,
  IconFolder,
  IconFolderPlus,
  IconPhoto,
  IconRefresh,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { FileActions } from '../components/file-actions';
import { FilePreview } from '../components/file-preview';
import { FolderActions } from '../components/folder-actions';
import { MediaReview } from '../components/media-review';
import {
  isReviewableMedia,
  mediaReviewFolderKey,
  summarizeMediaReview,
} from '../features/files/media-review';
import { getRecentUploads, sortFilesByDateUploaded } from '../features/files/recent-files';
import { uploadFile } from '../services/upload';
import { useAuthStore } from '../stores/auth-store';
import { type FilesViewMode, useFilesViewStore } from '../stores/files-view-store';
import { useMediaReviewStore } from '../stores/media-review-store';
import { apiHook } from '../treaty';
import { apiErrorMessage } from '../utils/api-error';

type UploadItem = {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'failed';
  error?: string;
  controller: AbortController;
};

type DriveItem = {
  entityType: 'file' | 'folder';
  id: string;
  name: string;
  parentId: string | null;
  revision: number;
};

type MoveTarget = {
  id: string | null;
  name: string;
};

type PendingMove = {
  item: DriveItem;
  target: MoveTarget;
};

const driveItemMimeType = 'application/x-disit-drive-item';

function responseErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('value' in error)) return null;
  const value = error.value;
  if (typeof value !== 'object' || value === null || !('code' in value)) return null;
  return typeof value.code === 'string' ? value.code : null;
}

function moveTargetKey(targetId: string | null): string {
  return targetId ?? 'root';
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unit;
  return `${value.toFixed(unit === 0 || value >= 10 ? 0 : 1)} ${units[unit]}`;
}

export function FilesPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const draggedItemRef = useRef<DriveItem | null>(null);
  const moveInFlight = useRef(false);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folder');
  const [dragging, setDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<DriveItem | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [movingItemId, setMovingItemId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewInitialFileId, setReviewInitialFileId] = useState<string | null>(null);
  const userId = useAuthStore((state) => state.user!.id);
  const viewMode = useFilesViewStore((state) => state.viewMode);
  const setViewMode = useFilesViewStore((state) => state.setViewMode);
  const reviewStatuses = useMediaReviewStore((state) => state.statuses);
  const reviewTargets = useMediaReviewStore((state) => state.targets);
  const contentsQuery = apiHook.useQuery(
    (client) => client['folder-contents'].get({ query: folderId ? { folderId } : {} }),
    { refetchInterval: () => (document.hidden ? 30_000 : 10_000) },
  );
  const allFilesQuery = apiHook.useQuery((client) => client.files.get(), {
    refetchInterval: () => (document.hidden ? 30_000 : 10_000),
  });
  const allFiles = useMemo(() => allFilesQuery.data ?? [], [allFilesQuery.data]);
  const mediaFiles = useMemo(
    () => contentsQuery.data?.files.filter(isReviewableMedia) ?? [],
    [contentsQuery.data?.files],
  );
  const sortedFilesByDate = useMemo(() => sortFilesByDateUploaded(allFiles), [allFiles]);
  const recentUploads = useMemo(() => getRecentUploads(allFiles, 3), [allFiles]);
  const reviewCounts = useMemo(
    () =>
      summarizeMediaReview(
        mediaFiles.map((file) => file.id),
        reviewStatuses,
        userId,
      ),
    [mediaFiles, reviewStatuses, userId],
  );
  const reviewTarget = reviewTargets[mediaReviewFolderKey(userId, folderId)] ?? null;

  async function optimisticallyRemoveFile(fileId: string) {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['folder-contents'] }),
      queryClient.cancelQueries({ queryKey: ['files'] }),
    ]);
    queryClient.setQueriesData(
      { queryKey: ['folder-contents'] },
      (contents: typeof contentsQuery.data) =>
        contents
          ? { ...contents, files: contents.files.filter((file) => file.id !== fileId) }
          : contents,
    );
    queryClient.setQueriesData({ queryKey: ['files'] }, (filesList: typeof allFilesQuery.data) =>
      filesList ? filesList.filter((file) => file.id !== fileId) : filesList,
    );
  }

  async function refreshFolderContents(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] }),
      queryClient.invalidateQueries({ queryKey: ['files'] }),
    ]);
  }

  function openFolder(id: string | null) {
    setSearchParams(id ? { folder: id } : {});
  }

  function isInternalDriveDrag(event: DragEvent<HTMLElement>): boolean {
    return (
      draggedItemRef.current !== null ||
      Array.from(event.dataTransfer.types).includes(driveItemMimeType)
    );
  }

  function startDriveDrag(event: DragEvent<HTMLElement>, item: DriveItem) {
    const target = event.target;
    if (target instanceof HTMLElement && target.closest('button[aria-label], a[aria-label]')) {
      event.preventDefault();
      return;
    }
    draggedItemRef.current = item;
    setDraggedItem(item);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(driveItemMimeType, item.id);
  }

  function finishDriveDrag() {
    draggedItemRef.current = null;
    setDraggedItem(null);
    setDropTarget(null);
  }

  function canMoveTo(item: DriveItem, targetId: string | null): boolean {
    return item.parentId !== targetId && !(item.entityType === 'folder' && item.id === targetId);
  }

  function handleMoveDragOver(event: DragEvent<HTMLElement>, target: MoveTarget) {
    const item = draggedItemRef.current;
    if (!item || !canMoveTo(item, target.id)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setDropTarget(moveTargetKey(target.id));
  }

  function handleMoveDragLeave(event: DragEvent<HTMLElement>, targetId: string | null) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    const targetKey = moveTargetKey(targetId);
    setDropTarget((current) => (current === targetKey ? null : current));
  }

  function handleMoveDrop(event: DragEvent<HTMLElement>, target: MoveTarget) {
    const item = draggedItemRef.current;
    if (!item || !canMoveTo(item, target.id)) return;
    event.preventDefault();
    event.stopPropagation();
    finishDriveDrag();
    void moveDriveItem(item, target);
  }

  function closeRenameModal() {
    if (moveInFlight.current) return;
    setPendingMove(null);
    setRenameName('');
    setRenameError(null);
  }

  async function moveDriveItem(
    item: DriveItem,
    target: MoveTarget,
    renamedTo?: string,
    isRenameAttempt = false,
  ) {
    if (moveInFlight.current) return;
    moveInFlight.current = true;
    setMovingItemId(item.id);
    if (isRenameAttempt) setRenameError(null);

    try {
      const response =
        item.entityType === 'file'
          ? await api.api.api.v1.files({ id: item.id }).patch({
              folderId: target.id,
              expectedRevision: item.revision,
              ...(renamedTo ? { name: renamedTo } : {}),
            })
          : await api.api.api.v1.folders({ id: item.id }).patch({
              parentId: target.id,
              expectedRevision: item.revision,
              ...(renamedTo ? { name: renamedTo } : {}),
            });

      if (response.error) {
        if (responseErrorCode(response.error) === 'name_conflict') {
          if (isRenameAttempt) {
            setRenameError('That name is already in use in the destination folder.');
          } else {
            setPendingMove({ item, target });
            setRenameName(item.name);
            setRenameError(null);
          }
          return;
        }

        const message = apiErrorMessage(response.error, {
          fallback: `“${item.name}” could not be moved.`,
          status: response.status,
        });
        if (isRenameAttempt) setRenameError(message);
        else notifications.show({ color: 'red', title: 'Move failed', message });
        return;
      }

      if (isRenameAttempt) {
        setPendingMove(null);
        setRenameName('');
        setRenameError(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      notifications.show({
        color: 'green',
        message: `${renamedTo ?? item.name} moved to ${target.name}.`,
      });
    } catch (error) {
      const message = apiErrorMessage(error, {
        fallback: `“${item.name}” could not be moved.`,
      });
      if (isRenameAttempt) setRenameError(message);
      else notifications.show({ color: 'red', title: 'Move failed', message });
    } finally {
      moveInFlight.current = false;
      setMovingItemId(null);
    }
  }

  async function createNewFolder() {
    const name = folderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      const response = await api.api.api.v1.folders.post({ name, parentId: folderId });
      if (response.error) {
        notifications.show({
          color: 'red',
          title: 'Folder creation failed',
          message: apiErrorMessage(response.error, {
            fallback: `The folder “${name}” could not be created.`,
            status: response.status,
          }),
        });
        return;
      }
      setFolderName('');
      setFolderModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Folder creation failed',
        message: apiErrorMessage(error, {
          fallback: `The folder “${name}” could not be created.`,
        }),
      });
    } finally {
      setCreatingFolder(false);
    }
  }

  async function startUpload(file: File) {
    const id = crypto.randomUUID();
    const controller = new AbortController();
    setUploads((current) => [
      ...current,
      { id, name: file.name, progress: 0, status: 'uploading', controller },
    ]);

    try {
      await uploadFile(
        file,
        (progress) =>
          setUploads((current) =>
            current.map((upload) => (upload.id === id ? { ...upload, progress } : upload)),
          ),
        controller.signal,
        folderId,
      );
      setUploads((current) => current.filter((upload) => upload.id !== id));
      await queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      notifications.show({ color: 'green', message: `${file.name} uploaded.` });
    } catch (error) {
      const cancelled = error instanceof DOMException && error.name === 'AbortError';
      if (cancelled) {
        setUploads((current) => current.filter((upload) => upload.id !== id));
        return;
      }
      const message = apiErrorMessage(error, {
        fallback: `Upload failed for “${file.name}”.`,
      });
      setUploads((current) =>
        current.map((upload) =>
          upload.id === id ? { ...upload, status: 'failed', error: message } : upload,
        ),
      );
      notifications.show({
        color: 'red',
        title: `Upload failed: ${file.name}`,
        message,
      });
    }
  }

  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    for (const file of fileList) void startUpload(file);
  }

  const currentFolderFileCount = contentsQuery.data?.files.length ?? 0;
  const currentFolderCount = contentsQuery.data?.folders.length ?? 0;
  const totalItemCount = currentFolderCount + currentFolderFileCount;
  const displayItemCount = viewMode === 'recent' ? allFiles.length : totalItemCount;

  return (
    <Stack gap='lg' maw={1200} mx='auto'>
      <Group justify='space-between' align='end'>
        <div>
          <Title order={1}>{contentsQuery.data?.currentFolder?.name ?? 'Files'}</Title>
          <Text c='dimmed' mt={5}>
            Upload files or drag files and folders into another folder to organize the workspace.
          </Text>
        </div>
        <Group gap='sm'>
          <Button
            variant='default'
            leftSection={<IconFolderPlus size={18} />}
            onClick={() => setFolderModalOpen(true)}
          >
            New folder
          </Button>
          <Button leftSection={<IconUpload size={18} />} onClick={() => inputRef.current?.click()}>
            Upload files
          </Button>
        </Group>
      </Group>

      <Breadcrumbs separator='›'>
        {contentsQuery.data?.breadcrumbs.map((breadcrumb) => (
          <Anchor
            component='button'
            type='button'
            key={breadcrumb.id ?? 'root'}
            className='drive-drop-target'
            data-drop-target={
              dropTarget === moveTargetKey(breadcrumb.id) &&
              draggedItem !== null &&
              canMoveTo(draggedItem, breadcrumb.id)
                ? true
                : undefined
            }
            fw={breadcrumb.id === folderId || (!breadcrumb.id && !folderId) ? 650 : 400}
            onClick={() => openFolder(breadcrumb.id)}
            onDragOver={(event) => handleMoveDragOver(event, breadcrumb)}
            onDragLeave={(event) => handleMoveDragLeave(event, breadcrumb.id)}
            onDrop={(event) => handleMoveDrop(event, breadcrumb)}
          >
            {breadcrumb.name}
          </Anchor>
        ))}
      </Breadcrumbs>

      <Modal
        opened={folderModalOpen}
        onClose={() => setFolderModalOpen(false)}
        title='Create folder'
        centered
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void createNewFolder();
          }}
        >
          <Stack>
            <TextInput
              label='Folder name'
              value={folderName}
              maxLength={255}
              autoFocus
              onChange={(event) => setFolderName(event.currentTarget.value)}
            />
            <Group justify='flex-end'>
              <Button variant='default' onClick={() => setFolderModalOpen(false)}>
                Cancel
              </Button>
              <Button type='submit' loading={creatingFolder} disabled={!folderName.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={pendingMove !== null}
        onClose={closeRenameModal}
        title='Rename to move'
        centered
        closeOnClickOutside={!moveInFlight.current}
        closeOnEscape={!moveInFlight.current}
      >
        {pendingMove && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const name = renameName.trim();
              if (!name || name === pendingMove.item.name) return;
              void moveDriveItem(pendingMove.item, pendingMove.target, name, true);
            }}
          >
            <Stack>
              <Text size='sm'>
                {pendingMove.target.name} already contains an item named “{pendingMove.item.name}”.
                Choose a different name to complete the move.
              </Text>
              <TextInput
                label='New name'
                value={renameName}
                error={renameError}
                maxLength={255}
                autoFocus
                onChange={(event) => {
                  setRenameName(event.currentTarget.value);
                  setRenameError(null);
                }}
              />
              <Group justify='flex-end'>
                <Button
                  variant='default'
                  onClick={closeRenameModal}
                  disabled={movingItemId !== null}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  loading={movingItemId !== null}
                  disabled={!renameName.trim() || renameName.trim() === pendingMove.item.name}
                >
                  Rename and move
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      <MediaReview
        folderId={folderId}
        folderName={contentsQuery.data?.currentFolder?.name ?? 'Files'}
        initialFileId={reviewInitialFileId}
        items={mediaFiles}
        opened={reviewOpen}
        userId={userId}
        onClose={() => setReviewOpen(false)}
      />

      <input
        ref={inputRef}
        type='file'
        multiple
        hidden
        onChange={(event) => {
          addFiles(event.currentTarget.files);
          event.currentTarget.value = '';
        }}
      />

      <Paper
        withBorder
        radius='lg'
        p='xl'
        className='upload-zone'
        data-dragging={dragging || undefined}
        onDragEnter={(event) => {
          event.preventDefault();
          if (isInternalDriveDrag(event)) return;
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!isInternalDriveDrag(event)) event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (isInternalDriveDrag(event)) return;
          addFiles(event.dataTransfer.files);
        }}
      >
        <Center>
          <Stack align='center' gap={6}>
            <IconFileUpload size={36} stroke={1.6} color='var(--mantine-color-blue-6)' />
            <Text fw={650}>Drop files here to upload</Text>
            <Text size='sm' c='dimmed'>
              Files will be added to {contentsQuery.data?.currentFolder?.name ?? 'the root folder'}.
            </Text>
          </Stack>
        </Center>
      </Paper>

      {uploads.length > 0 && (
        <Stack gap='xs'>
          <Text fw={650}>Uploads</Text>
          {uploads.map((upload) => (
            <Paper key={upload.id} withBorder radius='md' p='md'>
              <Group wrap='nowrap'>
                <IconFile size={22} />
                <Stack gap={5} style={{ flex: 1 }}>
                  <Group justify='space-between' gap='xs'>
                    <Text size='sm' fw={600} lineClamp={1}>
                      {upload.name}
                    </Text>
                    <Text size='xs' c={upload.status === 'failed' ? 'red' : 'dimmed'}>
                      {upload.status === 'failed' ? 'Failed' : `${upload.progress}%`}
                    </Text>
                  </Group>
                  {upload.error && (
                    <Text size='xs' c='red' style={{ overflowWrap: 'anywhere' }}>
                      {upload.error}
                    </Text>
                  )}
                  <Progress
                    value={upload.progress}
                    size='sm'
                    color={upload.status === 'failed' ? 'red' : 'blue'}
                  />
                </Stack>
                <ActionIcon
                  variant='subtle'
                  color='gray'
                  aria-label={`Cancel ${upload.name}`}
                  onClick={() => {
                    upload.controller.abort();
                    setUploads((current) => current.filter((item) => item.id !== upload.id));
                  }}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {recentUploads.length > 0 && (
        <Stack gap='xs'>
          <Group gap='xs'>
            <IconClock size={18} stroke={1.8} color='var(--mantine-color-blue-6)' />
            <Text fw={700} size='sm'>
              Recent uploads
            </Text>
            <Badge size='sm' variant='light'>
              {recentUploads.length}
            </Badge>
          </Group>
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing='md'>
            {recentUploads.map((file) => (
              <Paper
                key={`recent-top-${file.id}`}
                withBorder
                radius='lg'
                className='file-card drive-item'
                draggable={movingItemId === null}
                data-dragging={
                  draggedItem?.entityType === 'file' && draggedItem.id === file.id
                    ? true
                    : undefined
                }
                data-moving={movingItemId === file.id ? true : undefined}
                onDragStart={(event) =>
                  startDriveDrag(event, {
                    entityType: 'file',
                    id: file.id,
                    name: file.name,
                    parentId: file.folderId ?? null,
                    revision: file.revision,
                  })
                }
                onDragEnd={finishDriveDrag}
              >
                <FilePreview
                  fileId={file.id}
                  name={file.name}
                  mimeType={file.mimeType}
                  mode='card'
                  onOpenReview={() => {
                    setReviewInitialFileId(file.id);
                    setReviewOpen(true);
                  }}
                />
                <Stack gap={5} p='md'>
                  <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Text fw={650} lineClamp={1} title={file.name} style={{ minWidth: 0 }}>
                      {file.name}
                    </Text>
                    <FileActions
                      fileId={file.id}
                      name={file.name}
                      mimeType={file.mimeType}
                      size={file.size}
                      onDeleteStarted={() => optimisticallyRemoveFile(file.id)}
                      onDeleteFailed={refreshFolderContents}
                      onDeleted={refreshFolderContents}
                    />
                  </Group>
                  <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Text size='xs' c='dimmed'>
                      {formatBytes(file.size)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {dayjs(file.createdAt).format('DD MMM YYYY, HH:mm')}
                    </Text>
                  </Group>
                  <Text size='xs' c='dimmed' lineClamp={1}>
                    {file.uploadedBy.email}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Stack>
      )}

      <Stack gap='sm'>
        <Group justify='space-between'>
          <Group gap='sm'>
            <Text fw={700}>Contents</Text>
            <Badge variant='light'>{displayItemCount}</Badge>
          </Group>
          <Group gap='xs'>
            {mediaFiles.length > 0 && (
              <Button
                size='xs'
                variant='light'
                leftSection={<IconPhoto size={17} />}
                rightSection={
                  <Badge
                    size='sm'
                    variant='filled'
                    color={
                      reviewTarget !== null && reviewCounts.shortlisted > reviewTarget
                        ? 'red'
                        : 'blue'
                    }
                  >
                    {reviewCounts.shortlisted}
                    {reviewTarget === null ? '' : `/${reviewTarget}`}
                  </Badge>
                }
                onClick={() => {
                  setReviewInitialFileId(null);
                  setReviewOpen(true);
                }}
              >
                Review media
              </Button>
            )}
            <SegmentedControl
              size='xs'
              value={viewMode}
              aria-label='File view'
              data={[
                { label: 'List', value: 'list' },
                { label: 'Cards', value: 'cards' },
                { label: 'Recent', value: 'recent' },
              ]}
              onChange={(value) => setViewMode(value as FilesViewMode)}
            />
            <ActionIcon
              variant='subtle'
              aria-label='Refresh folder'
              loading={contentsQuery.isFetching}
              onClick={() => void contentsQuery.refetch()}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>

        {contentsQuery.isError && (
          <Alert
            color='red'
            title='Folder contents could not be loaded'
            icon={<IconAlertCircle size={18} />}
          >
            {apiErrorMessage(contentsQuery.error, {
              fallback: 'The file list could not be loaded.',
            })}
          </Alert>
        )}

        {!contentsQuery.isLoading && displayItemCount === 0 && (
          <Paper withBorder radius='lg'>
            <Center py={60}>
              <Stack align='center' gap={4}>
                {viewMode === 'recent' ? (
                  <>
                    <IconFile size={34} stroke={1.4} color='var(--mantine-color-gray-5)' />
                    <Text fw={600}>No files uploaded yet</Text>
                    <Text size='sm' c='dimmed'>
                      Upload files to see them sorted by upload date.
                    </Text>
                  </>
                ) : (
                  <>
                    <IconFolder size={34} stroke={1.4} color='var(--mantine-color-gray-5)' />
                    <Text fw={600}>This folder is empty</Text>
                    <Text size='sm' c='dimmed'>
                      Create a folder or upload files here.
                    </Text>
                  </>
                )}
              </Stack>
            </Center>
          </Paper>
        )}

        {viewMode === 'list' && totalItemCount > 0 && (
          <Paper withBorder radius='lg' className='data-card'>
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing='sm' horizontalSpacing='lg' highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Size</Table.Th>
                    <Table.Th>Created by</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th ta='right'>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {contentsQuery.data?.folders.map((folder) => (
                    <Table.Tr
                      key={folder.id}
                      className='folder-row drive-item drive-drop-target'
                      draggable={movingItemId === null}
                      data-dragging={
                        draggedItem?.entityType === 'folder' && draggedItem.id === folder.id
                          ? true
                          : undefined
                      }
                      data-drop-target={dropTarget === moveTargetKey(folder.id) ? true : undefined}
                      data-moving={movingItemId === folder.id ? true : undefined}
                      onDragStart={(event) =>
                        startDriveDrag(event, {
                          entityType: 'folder',
                          id: folder.id,
                          name: folder.name,
                          parentId: folderId,
                          revision: folder.revision,
                        })
                      }
                      onDragEnd={finishDriveDrag}
                      onDragOver={(event) =>
                        handleMoveDragOver(event, { id: folder.id, name: folder.name })
                      }
                      onDragLeave={(event) => handleMoveDragLeave(event, folder.id)}
                      onDrop={(event) =>
                        handleMoveDrop(event, { id: folder.id, name: folder.name })
                      }
                      onDoubleClick={() => openFolder(folder.id)}
                    >
                      <Table.Td>
                        <Anchor component='button' onClick={() => openFolder(folder.id)}>
                          <Group gap='sm' wrap='nowrap'>
                            <Center className='file-preview file-preview-list folder-preview'>
                              <IconFolder size={26} stroke={1.5} />
                            </Center>
                            <Text fw={600}>{folder.name}</Text>
                          </Group>
                        </Anchor>
                      </Table.Td>
                      <Table.Td>—</Table.Td>
                      <Table.Td>{folder.createdBy.email}</Table.Td>
                      <Table.Td>{dayjs(folder.createdAt).format('DD MMM YYYY, HH:mm')}</Table.Td>
                      <Table.Td>
                        <Group justify='flex-end'>
                          <FolderActions
                            folderId={folder.id}
                            name={folder.name}
                            onDeleted={() =>
                              queryClient.invalidateQueries({ queryKey: ['folder-contents'] })
                            }
                          />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {contentsQuery.data?.files.map((file) => (
                    <Table.Tr
                      key={file.id}
                      className='drive-item'
                      draggable={movingItemId === null}
                      data-dragging={
                        draggedItem?.entityType === 'file' && draggedItem.id === file.id
                          ? true
                          : undefined
                      }
                      data-moving={movingItemId === file.id ? true : undefined}
                      onDragStart={(event) =>
                        startDriveDrag(event, {
                          entityType: 'file',
                          id: file.id,
                          name: file.name,
                          parentId: folderId,
                          revision: file.revision,
                        })
                      }
                      onDragEnd={finishDriveDrag}
                    >
                      <Table.Td>
                        <Group gap='sm' wrap='nowrap'>
                          <FilePreview
                            fileId={file.id}
                            name={file.name}
                            mimeType={file.mimeType}
                            mode='list'
                            onOpenReview={() => {
                              setReviewInitialFileId(file.id);
                              setReviewOpen(true);
                            }}
                          />
                          <Text fw={600} lineClamp={1}>
                            {file.name}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>{formatBytes(file.size)}</Table.Td>
                      <Table.Td>{file.uploadedBy.email}</Table.Td>
                      <Table.Td>{dayjs(file.createdAt).format('DD MMM YYYY, HH:mm')}</Table.Td>
                      <Table.Td>
                        <Group justify='flex-end'>
                          <FileActions
                            fileId={file.id}
                            name={file.name}
                            mimeType={file.mimeType}
                            size={file.size}
                            onDeleteStarted={() => optimisticallyRemoveFile(file.id)}
                            onDeleteFailed={refreshFolderContents}
                            onDeleted={refreshFolderContents}
                          />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}

        {viewMode === 'cards' && totalItemCount > 0 && (
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 4 }} spacing='md'>
            {contentsQuery.data?.folders.map((folder) => (
              <Paper
                key={folder.id}
                withBorder
                radius='lg'
                className='file-card folder-card drive-item drive-drop-target'
                role='button'
                tabIndex={0}
                draggable={movingItemId === null}
                data-dragging={
                  draggedItem?.entityType === 'folder' && draggedItem.id === folder.id
                    ? true
                    : undefined
                }
                data-drop-target={dropTarget === moveTargetKey(folder.id) ? true : undefined}
                data-moving={movingItemId === folder.id ? true : undefined}
                onDragStart={(event) =>
                  startDriveDrag(event, {
                    entityType: 'folder',
                    id: folder.id,
                    name: folder.name,
                    parentId: folderId,
                    revision: folder.revision,
                  })
                }
                onDragEnd={finishDriveDrag}
                onDragOver={(event) =>
                  handleMoveDragOver(event, { id: folder.id, name: folder.name })
                }
                onDragLeave={(event) => handleMoveDragLeave(event, folder.id)}
                onDrop={(event) => handleMoveDrop(event, { id: folder.id, name: folder.name })}
                onClick={() => openFolder(folder.id)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') openFolder(folder.id);
                }}
              >
                <Center className='file-preview file-preview-card folder-preview'>
                  <IconFolder size={64} stroke={1.25} />
                </Center>
                <Stack gap={5} p='md' ta='left'>
                  <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Text fw={650} lineClamp={1}>
                      {folder.name}
                    </Text>
                    <FolderActions
                      folderId={folder.id}
                      name={folder.name}
                      onDeleted={() =>
                        queryClient.invalidateQueries({ queryKey: ['folder-contents'] })
                      }
                    />
                  </Group>
                  <Text size='xs' c='dimmed'>
                    Folder · {folder.createdBy.email}
                  </Text>
                </Stack>
              </Paper>
            ))}
            {contentsQuery.data?.files.map((file) => (
              <Paper
                key={file.id}
                withBorder
                radius='lg'
                className='file-card drive-item'
                draggable={movingItemId === null}
                data-dragging={
                  draggedItem?.entityType === 'file' && draggedItem.id === file.id
                    ? true
                    : undefined
                }
                data-moving={movingItemId === file.id ? true : undefined}
                onDragStart={(event) =>
                  startDriveDrag(event, {
                    entityType: 'file',
                    id: file.id,
                    name: file.name,
                    parentId: folderId,
                    revision: file.revision,
                  })
                }
                onDragEnd={finishDriveDrag}
              >
                <FilePreview
                  fileId={file.id}
                  name={file.name}
                  mimeType={file.mimeType}
                  mode='card'
                  onOpenReview={() => {
                    setReviewInitialFileId(file.id);
                    setReviewOpen(true);
                  }}
                />
                <Stack gap={5} p='md'>
                  <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Text fw={650} lineClamp={1} title={file.name} style={{ minWidth: 0 }}>
                      {file.name}
                    </Text>
                    <FileActions
                      fileId={file.id}
                      name={file.name}
                      mimeType={file.mimeType}
                      size={file.size}
                      onDeleteStarted={() => optimisticallyRemoveFile(file.id)}
                      onDeleteFailed={refreshFolderContents}
                      onDeleted={refreshFolderContents}
                    />
                  </Group>
                  <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Text size='xs' c='dimmed'>
                      {formatBytes(file.size)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {dayjs(file.createdAt).format('DD MMM YYYY')}
                    </Text>
                  </Group>
                  <Text size='xs' c='dimmed' lineClamp={1}>
                    {file.uploadedBy.email}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        )}

        {viewMode === 'recent' && allFiles.length > 0 && (
          <Paper withBorder radius='lg' className='data-card'>
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing='sm' horizontalSpacing='lg' highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Size</Table.Th>
                    <Table.Th>Uploaded by</Table.Th>
                    <Table.Th>Uploaded at</Table.Th>
                    <Table.Th ta='right'>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedFilesByDate.map((file) => (
                    <Table.Tr
                      key={file.id}
                      className='drive-item'
                      draggable={movingItemId === null}
                      data-dragging={
                        draggedItem?.entityType === 'file' && draggedItem.id === file.id
                          ? true
                          : undefined
                      }
                      data-moving={movingItemId === file.id ? true : undefined}
                      onDragStart={(event) =>
                        startDriveDrag(event, {
                          entityType: 'file',
                          id: file.id,
                          name: file.name,
                          parentId: file.folderId ?? null,
                          revision: file.revision,
                        })
                      }
                      onDragEnd={finishDriveDrag}
                    >
                      <Table.Td>
                        <Group gap='sm' wrap='nowrap'>
                          <FilePreview
                            fileId={file.id}
                            name={file.name}
                            mimeType={file.mimeType}
                            mode='list'
                            onOpenReview={() => {
                              setReviewInitialFileId(file.id);
                              setReviewOpen(true);
                            }}
                          />
                          <Text fw={650} lineClamp={1}>
                            {file.name}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>{formatBytes(file.size)}</Table.Td>
                      <Table.Td>{file.uploadedBy.email}</Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap='nowrap'>
                          <IconClock size={15} color='var(--mantine-color-dimmed)' />
                          <Text size='sm'>
                            {dayjs(file.createdAt).format('DD MMM YYYY, HH:mm')}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group justify='flex-end'>
                          <FileActions
                            fileId={file.id}
                            name={file.name}
                            mimeType={file.mimeType}
                            size={file.size}
                            onDeleteStarted={() => optimisticallyRemoveFile(file.id)}
                            onDeleteFailed={refreshFolderContents}
                            onDeleted={refreshFolderContents}
                          />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}
      </Stack>
    </Stack>
  );
}
